import React from 'react'
import { useList } from '@refinedev/core'
import { DateField } from '@refinedev/antd'
import { Button, Drawer, Table, Tag } from 'antd'
import { brand } from '../../theme/brand'
import { TableSurface, EmptyHint } from '../../theme/listkit'
import { monthLabel, spotlightState, type SpotlightState } from './botm'
import { SpotlightForm, type SpotlightRecord } from './SpotlightForm'

const SELECT = '*, business:businesses(id,name, owner:profiles!businesses_owner_profile_fkey(name,class_year))'

const STATUS_TAG: Record<SpotlightState, { color?: string; label: string }> = {
  draft: { color: 'gold', label: 'Draft' },
  scheduled: { color: 'blue', label: 'Scheduled' },
  live: { color: 'green', label: 'Live' },
  past: { label: 'Past' },
}

/**
 * Schedule tab — every spotlight, newest month first, with its live status
 * (`spotlightState`, the same draft/scheduled/live/past machine as releases) and
 * an Edit action that opens `SpotlightForm` in a Drawer. "Schedule a spotlight"
 * opens the same Drawer empty, defaulting to the console's currently selected
 * month.
 */
export function ScheduleTab({ month }: { month: string }) {
  const { result, query } = useList<SpotlightRecord>({
    resource: 'business_spotlights',
    sorters: [{ field: 'month', order: 'desc' }],
    pagination: { pageSize: 24 },
    meta: { select: SELECT },
  })
  const rows = result?.data ?? []

  // undefined = drawer closed; null = new spotlight; a record = editing that row.
  const [editing, setEditing] = React.useState<SpotlightRecord | null | undefined>(undefined)
  const open = editing !== undefined
  const showEmpty = !query.isLoading && rows.length === 0

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <Button type="primary" data-testid="spotlight-new" onClick={() => setEditing(null)}>
          Schedule a spotlight
        </Button>
      </div>

      {showEmpty ? (
        <EmptyHint>No spotlights scheduled yet.</EmptyHint>
      ) : (
        <TableSurface>
          <Table<SpotlightRecord>
            dataSource={rows}
            loading={query.isLoading}
            rowKey="id"
            pagination={false}
            scroll={{ x: true }}
          >
            <Table.Column<SpotlightRecord>
              dataIndex="month"
              title="Month"
              render={(m: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{monthLabel(m)}</span>
              )}
            />
            <Table.Column<SpotlightRecord>
              title="Business"
              render={(_: unknown, r) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 13.5, color: brand.ink }}>
                  {r.business?.name ?? '—'}
                </span>
              )}
            />
            <Table.Column<SpotlightRecord>
              title="Status"
              render={(_: unknown, r) => {
                const state = spotlightState(r.published_at, r.month, new Date())
                const tag = STATUS_TAG[state]
                return <Tag color={tag.color}>{tag.label}</Tag>
              }}
            />
            <Table.Column<SpotlightRecord>
              dataIndex="published_at"
              title="Publishes"
              render={(value: string | null) =>
                value ? (
                  <DateField value={value} format="MMM D, YYYY h:mm A" />
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<SpotlightRecord>
              title="Actions"
              render={(_: unknown, r) => (
                <Button size="small" onClick={() => setEditing(r)}>
                  Edit
                </Button>
              )}
            />
          </Table>
        </TableSurface>
      )}

      <Drawer
        title={editing ? 'Edit spotlight' : 'Schedule a spotlight'}
        width={640}
        open={open}
        onClose={() => setEditing(undefined)}
        destroyOnHidden
      >
        {open ? (
          <SpotlightForm
            key={editing?.id ?? 'new'}
            initial={editing ?? undefined}
            month={editing?.month ?? month}
            onDone={() => setEditing(undefined)}
          />
        ) : null}
      </Drawer>
    </div>
  )
}
